<template>
  <li
    class="nav-item softphone-header"
    data-cy="softphone-header"
  >
    <!--
      The collapsed softphone. A plain button rather than a b-nav-item so the expanded state can be
      announced properly - this is a disclosure, not a link.
    -->
    <button
      ref="toggle"
      type="button"
      class="btn btn-link bg-transparent softphone-header__toggle d-flex flex-column align-items-center"
      :class="{ 'softphone-header__toggle--ringing': isRinging }"
      :title="toggleTitle"
      :aria-expanded="String(isExpanded)"
      aria-controls="softphone-header-panel"
      data-cy="softphone-header-toggle"
      @click="toggle"
    >
      <font-awesome-icon
        :icon="['fa', toggleIcon]"
        class="softphone-header__icon"
      />
      <span class="softphone-header__label">Softphone</span>

      <!--
        The same alert dot the Support tab uses, for the same reason: this is the notification that
        reaches an agent who has the panel collapsed and is working somewhere else on the page. It
        shows in every tab, not just the one on the call, because a call the agent cannot see is
        exactly the thing worth pointing at.
      -->
      <span
        v-if="isRinging"
        class="softphone-header__alert"
        data-cy="softphone-header-alert"
      >
        <font-awesome-icon :icon="['fa', 'phone-volume']" />
      </span>

      <span
        v-else-if="statusDotClass"
        class="softphone-header__dot"
        :class="statusDotClass"
        data-cy="softphone-header-dot"
      />
    </button>

    <div
      v-if="isExpanded"
      id="softphone-header-panel"
      ref="panel"
      class="softphone-header__panel"
      data-cy="softphone-header-panel"
    >
      <!--
        The panel closes behind Go offline. The agent has said they are done taking calls for now,
        and the panel is where they say it - leaving it open in front of them afterwards reads as
        the click not having landed. Nothing else in the widget closes it: an agent mid-call keeps
        the panel they opened.
      -->
      <SoftphoneWidget @went-offline="collapse" />
    </div>
  </li>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import SoftphoneWidget from '@/components/softphone/SoftphoneWidget.vue'
import { armRingtone, isRingtoneArmed } from '@/services/softphone/ringtone'
import { SOFTPHONE_STATUS } from '@/store/modules/softphone.store'

/**
 * What the browser tab's title wears, as a MARKER on whatever the title happens to be rather than a
 * replacement for it, so the page's own title is never lost.
 *
 * The same states the dot on the collapsed header shows, said where an agent working in a different
 * tab can read them - the header is only visible in the tab they are looking at. A title is plain
 * text, so the colour is the emoji's own: these glyphs are the palette, not a choice.
 *
 * Only the incoming call gets words. The rest are one glyph: they are a state the agent glances at,
 * not news, and a tab strip gives a title about a dozen characters before it cuts it off.
 */
const TITLE_MARKERS = Object.freeze({
  ringing: '📞 Incoming call · ',
  // The same phone as the ringing marker, standing still and saying nothing: it is the same call,
  // and the difference between one being offered and one being on is that the tab stopped flashing.
  onCall: '📞 ',
  ready: '🟢 ',
  // Connecting and the wrap-up: taking calls, but not this second.
  waiting: '🟡 ',
  // The error that leaves an agent not taking calls without their asking. Offline gets NOTHING: it
  // is the state the app is in nearly all the time, for nearly every user, and a marker that is
  // almost always there says nothing when it is. Unicode has no grey circle - the pale one is as
  // close as a title can get, and the point of it is that it is the marker with no colour in it.
  error: '⚪ '
})

// Longest first, so the ringing marker is recognised as itself rather than as the On call marker it
// happens to begin with.
const ALL_TITLE_MARKERS = Object.freeze(
  Object.values(TITLE_MARKERS).sort((a, b) => b.length - a.length)
)

function markerOn(title) {
  return ALL_TITLE_MARKERS.find(marker => title.startsWith(marker)) || ''
}

function withoutMarker(title) {
  return title.slice(markerOn(title).length)
}

// Whatever is on the title now comes off first: the tab goes from Ready to On call without passing
// through a state that has no marker, and two glyphs stacked up would be one state too many.
function withMarker(title, marker) {
  return `${marker}${withoutMarker(title)}`
}

export default {
  name: 'SoftphoneHeaderWidget',

  components: {
    SoftphoneWidget
  },

  data() {
    return {
      isExpanded: false,
      titleFlashIntervalId: null,
      onDocumentPointerDown: null,
      onDocumentKeyDown: null,
      onDocumentVisibilityChange: null,
      retryArm: null
    }
  },

  computed: {
    ...mapState('softphone', ['status']),

    ...mapGetters('softphone', [
      'hasSoftphoneResumePrompt',
      'isCallActive',
      'isRinging',
      'isSoftphoneOnline',
      'isSoftphoneOwner'
    ]),

    // The panel opens itself on each of these, so they are watched as one thing - see the watcher.
    softphoneAttention() {
      return {
        isRinging: this.isRinging,
        isOnline: this.isSoftphoneOnline,
        isOwner: this.isSoftphoneOwner,
        hasResumePrompt: this.hasSoftphoneResumePrompt
      }
    },

    toggleIcon() {
      if (this.isRinging) {
        return 'phone-volume'
      }

      return this.isSoftphoneOnline ? 'phone' : 'phone-slash'
    },

    toggleTitle() {
      if (this.isRinging) {
        return 'Incoming call'
      }

      if (this.isCallActive) {
        return 'On a call'
      }

      return this.isExpanded ? 'Hide the softphone' : 'Show the softphone'
    },

    /**
     * What the tab's title wears while nothing is ringing: the same states as the dot on the
     * collapsed header, in the same order of precedence.
     *
     * Only in the tab that OWNS the softphone. A mirror renders the owner's state faithfully - that
     * is the point of it - but a green circle on three tabs at once says the agent is taking calls
     * in three places, and the one tab that IS taking them is then the hardest to find. The ringing
     * marker is deliberately the other way round: see `syncTitleMarker`.
     */
    steadyTitleMarker() {
      if (!this.isSoftphoneOwner) {
        return ''
      }

      if (this.isCallActive) {
        return TITLE_MARKERS.onCall
      }

      if (this.status === SOFTPHONE_STATUS.READY) {
        return TITLE_MARKERS.ready
      }

      const isWaiting = this.status === SOFTPHONE_STATUS.CONNECTING
        || this.status === SOFTPHONE_STATUS.WRAP_UP

      if (isWaiting) {
        return TITLE_MARKERS.waiting
      }

      return this.status === SOFTPHONE_STATUS.ERROR ? TITLE_MARKERS.error : ''
    },

    // Everything the title reacts to, watched as one thing - see the watcher.
    titleState() {
      return {
        isRinging: this.isRinging,
        marker: this.steadyTitleMarker
      }
    },

    /**
     * A colour on the collapsed icon, so an agent who never opens the panel can still see at a
     * glance that they are taking calls. Offline shows nothing: a grey dot on a grey header reads
     * as a rendering artefact rather than a state.
     */
    statusDotClass() {
      if (this.isCallActive) {
        return 'softphone-header__dot--active'
      }

      if (this.status === SOFTPHONE_STATUS.WRAP_UP) {
        return 'softphone-header__dot--wrap-up'
      }

      if (this.status === SOFTPHONE_STATUS.READY) {
        return 'softphone-header__dot--ready'
      }

      if (this.status === SOFTPHONE_STATUS.CONNECTING) {
        return 'softphone-header__dot--connecting'
      }

      if (this.status === SOFTPHONE_STATUS.ERROR) {
        return 'softphone-header__dot--error'
      }

      return ''
    }
  },

  watch: {
    /**
     * When the panel opens itself, and when the tab title starts flashing.
     *
     * Three things open it, all in the tab that OWNS the softphone and none of them in a mirror -
     * a panel springing open in three tabs at once, two of them with dead buttons, is noise:
     *
     * - A call arriving. The agent has seconds to decide, and hunting for a collapsed widget is
     *   not how they should spend them.
     * - Going live. This is the confirmation that the agent is taking calls; it replaced a success
     *   toast, which said the same thing in a place the agent had to read and then dismiss.
     * - A resume offer, after this tab inherited the softphone from one that went away. Unopened,
     *   the offer is invisible and the agent simply stops receiving calls with no idea why.
     */
    softphoneAttention({ isRinging, isOnline, isOwner, hasResumePrompt }, previous = {}) {
      const isNewlyRinging = isRinging && !previous.isRinging
      const isNewlyOnline = isOnline && !previous.isOnline

      if (isOwner && (isNewlyRinging || isNewlyOnline)) {
        this.expand()
      }

      if (hasResumePrompt && !previous.hasResumePrompt) {
        this.expand()
      }

    },

    // Every state the title says something about, including the ones the panel ignores.
    titleState() {
      this.syncTitleMarker()
    },

    /**
     * The router's own title mixin rewrites `document.title` on navigation, taking the marker with
     * it. Put back on the tick after, so the page has set its title first.
     */
    $route() {
      this.$nextTick(this.syncTitleMarker)
    }
  },

  mounted() {
    // The ringtone needs a user gesture before it can play, and a ringtone that stays silent is the
    // one failure an agent cannot see coming. Any interaction anywhere in the tab will do, and this
    // tab may not own the softphone yet when that interaction happens.
    this.armOnFirstInteraction()

    // A hidden tab is the whole reason the title flashes, so the flash follows the tab's visibility
    // as well as the call's state.
    this.onDocumentVisibilityChange = () => this.syncTitleMarker()
    document.addEventListener('visibilitychange', this.onDocumentVisibilityChange)

    // Not just a no-op on a fresh mount: a call can already be ringing when this instance is
    // created (a route change re-creates the header), and a watcher does not fire for state that
    // was already true. It also clears a marker left on the title by an instance that was
    // destroyed mid-flash.
    this.syncTitleMarker()
  },

  beforeDestroy() {
    this.clearTitleMarker()
    this.removeDismissListeners()
    this.removeArmListeners()

    if (this.onDocumentVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange)
      this.onDocumentVisibilityChange = null
    }
  },

  methods: {
    toggle() {
      if (this.isExpanded) {
        this.collapse()

        return
      }

      this.expand()
    },

    expand() {
      if (this.isExpanded) {
        return
      }

      this.isExpanded = true

      this.addDismissListeners()
    },

    collapse() {
      if (!this.isExpanded) {
        return
      }

      this.isExpanded = false

      this.removeDismissListeners()
    },

    /**
     * Click-outside and Escape, the two ways a header panel is expected to close. Deliberately not
     * closed by anything the softphone itself does: an agent mid-call keeps the panel they opened.
     */
    addDismissListeners() {
      if (this.onDocumentPointerDown) {
        return
      }

      this.onDocumentPointerDown = event => {
        const isInside = this.$el.contains(event.target)

        if (!isInside) {
          this.collapse()
        }
      }

      this.onDocumentKeyDown = event => {
        if (event.key !== 'Escape') {
          return
        }

        this.collapse()

        if (this.$refs.toggle) {
          this.$refs.toggle.focus()
        }
      }

      document.addEventListener('pointerdown', this.onDocumentPointerDown)
      document.addEventListener('keydown', this.onDocumentKeyDown)
    },

    removeDismissListeners() {
      if (this.onDocumentPointerDown) {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown)
        this.onDocumentPointerDown = null
      }

      if (this.onDocumentKeyDown) {
        document.removeEventListener('keydown', this.onDocumentKeyDown)
        this.onDocumentKeyDown = null
      }
    },

    armOnFirstInteraction() {
      if (isRingtoneArmed()) {
        return
      }

      this.retryArm = async () => {
        await armRingtone()

        if (isRingtoneArmed()) {
          this.removeArmListeners()
        }
      }

      window.addEventListener('pointerdown', this.retryArm)
      window.addEventListener('keydown', this.retryArm)
    },

    removeArmListeners() {
      if (!this.retryArm) {
        return
      }

      window.removeEventListener('pointerdown', this.retryArm)
      window.removeEventListener('keydown', this.retryArm)

      this.retryArm = null
    },

    /**
     * What the tab's title says, decided from scratch each time rather than toggled - which is what
     * lets it be called from a watcher, from `visibilitychange`, from a navigation and from
     * `mounted` without any of them needing to know what the others did.
     *
     * A ringing call is the one thing that FLASHES, and only in a HIDDEN tab. It is a notification
     * for an agent who is looking somewhere else, and the title is the only one that reaches them
     * there; a tab they are looking at already has the panel open in front of them, so a flashing
     * title would be noise - and one that outlives the call is worse than noise. Unlike the steady
     * markers it flashes in EVERY tab, owner or not: which tab holds the transport is an
     * implementation detail, and the call has to reach the agent in whichever one they are not
     * looking at.
     *
     * Everything else is a steady glyph, and a state rather than news: the tab may as well carry it
     * while the agent is looking at it.
     */
    syncTitleMarker() {
      if (this.isRinging && document.hidden) {
        this.startTitleFlash()

        return
      }

      this.stopTitleFlash()

      document.title = withMarker(document.title, this.steadyTitleMarker)
    },

    // The title as the page itself wrote it. For a tab that is going away, or one that has nothing
    // left to say about the softphone.
    clearTitleMarker() {
      this.stopTitleFlash()

      document.title = withoutMarker(document.title)
    },

    /**
     * The marker is applied to, and stripped from, whatever `document.title` IS at the time - not
     * restored from a title captured when the flash began.
     *
     * A captured title goes stale. The router's own title mixin rewrites the title on every
     * navigation, and a header re-created during a route change would capture the flashing title
     * itself and later "restore" it, leaving the tab reading Incoming for a call that ended long
     * ago. Reading the live title on every tick cannot get stuck that way.
     */
    startTitleFlash() {
      if (this.titleFlashIntervalId) {
        return
      }

      document.title = withMarker(document.title, TITLE_MARKERS.ringing)

      this.titleFlashIntervalId = setInterval(() => {
        document.title = markerOn(document.title) === TITLE_MARKERS.ringing
          ? withoutMarker(document.title)
          : withMarker(document.title, TITLE_MARKERS.ringing)
      }, 1000)
    },

    // The timer only. What the title should say instead is the caller's business - see
    // `syncTitleMarker`, which puts the steady marker on, and `clearTitleMarker`, which takes
    // everything off.
    stopTitleFlash() {
      if (this.titleFlashIntervalId) {
        clearInterval(this.titleFlashIntervalId)
        this.titleFlashIntervalId = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.softphone-header {
  position: relative;
  margin-right: 1rem;
}

.softphone-header__toggle {
  position: relative;
  padding: 0.25rem 0.5rem;
  color: var(--component-sidebar__toggle-font-color);
  text-decoration: none;

  &:hover,
  &:focus {
    color: var(--component-sidebar__toggle-font-color);
    text-decoration: none;
  }
}

.softphone-header__icon {
  font-size: 1rem;
}

.softphone-header__label {
  font-size: 0.8rem;
}

/* Matches the Support tab's unread badge - same corner, same size, same meaning. */
.softphone-header__alert {
  position: absolute;
  right: 2px;
  top: -2px;
  font-size: 7px;
  color: #fff;
  text-align: center;
  line-height: 14px;
  width: 15px;
  height: 14px;
  background: red;
  border-radius: 50%;
  animation: softphone-header-pulse 1s ease-in-out infinite;
}

.softphone-header__dot {
  position: absolute;
  right: 6px;
  top: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.softphone-header__dot--ready {
  background-color: #28a745;
}

.softphone-header__dot--connecting {
  background-color: #fd7e14;
}

.softphone-header__dot--active {
  background-color: #0d6efd;
}

.softphone-header__dot--wrap-up {
  background-color: #ffc107;
}

.softphone-header__dot--error {
  background-color: #dc3545;
}

/**
 * Anchored to the header rather than floated over the page: the softphone belongs to the chrome,
 * and an agent scrolling a campaigns list must not lose the call they are on.
 */
.softphone-header__panel {
  position: absolute;
  z-index: $zindex-dropdown;
  top: calc(100% + 0.5rem);
  right: 0;
  display: flex;
  flex-direction: column;
  width: 320px;
  padding: 1rem;
  border-radius: 0.375rem;
  /* The same surface the account switcher's menu uses, so the two read as one header. */
  background-color: $dropdown-bg;
  color: $dropdown-link-color;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.3);
}

@keyframes softphone-header-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
</style>
